/**
 * DashboardHero
 * ─────────────
 * Cabecera tipográfica del /admin Dashboard. Reemplaza los 9 KpiChips
 * por: eyebrow → saludo → número gigante con tri-gradient → micro-stats
 * con slashes. A la derecha: search (firma cyan 1.5px) y botón "Nueva"
 * (anillo cyan→green).
 *
 * El search es controlado — el debounce vive en el padre.
 */
import React, { useEffect, useRef, useState } from 'react';
import { dashboardHeroCSS } from './DashboardHero.css';
import { GradientRingButton } from '../../../Components/GradientRingButton';

const useCountUp = (target, duration = 900) => {
    const safeTarget = Number.isFinite(Number(target)) ? Math.round(Number(target)) : 0;
    const [value, setValue] = useState(0);
    const fromRef = useRef(0);
    const rafRef = useRef(null);

    useEffect(() => {
        const from = fromRef.current;
        if (from === safeTarget) {
            setValue(safeTarget);
            return undefined;
        }
        const delta = safeTarget - from;
        const start = performance.now();

        const tick = (now) => {
            const elapsed = now - start;
            const t = Math.min(1, elapsed / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(from + delta * eased));
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                fromRef.current = safeTarget;
                rafRef.current = null;
            }
        };

        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [safeTarget, duration]);

    return value;
};

const MESES_ES = [
    'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
    'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

const formatFechaEyebrow = () => {
    const d = new Date();
    return `${d.getDate()} ${MESES_ES[d.getMonth()]}`;
};

const formatPipelineMXN = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return `$${m >= 10 ? m.toFixed(1) : m.toFixed(2)}M`;
    }
    if (n >= 1_000) {
        return `$${Math.round(n / 1_000)}K`;
    }
    return `$${n.toLocaleString('es-MX')}`;
};

const firstName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return null;
    return fullName.trim().split(/\s+/)[0];
};

const DashboardHero = ({
    total = 0,
    pipelineMXN = 0,
    vendorFilter = null,
    userName = 'Admin',
    onSearchChange,
    searchValue = '',
    loading = false,
    onNewClick = null,
    lastUpdatedLabel = null,
    searchPlaceholder = 'Buscar por nombre, correo o unidad',
}) => {
    const first = firstName(userName) ?? 'Admin';
    const fecha = formatFechaEyebrow();
    const animatedTotal = useCountUp(total);
    const eyebrowParts = [
        'DASHBOARD',
        fecha,
        ...(lastUpdatedLabel ? [String(lastUpdatedLabel).toUpperCase()] : []),
    ];
    const showPipeline = Number(pipelineMXN) > 0;

    return (
        <div css={dashboardHeroCSS()}>
            <div className="dh-left">
                <div className="dh-eyebrow">{eyebrowParts.join(' · ')}</div>
                <div className="dh-greeting">Hola, {first}.</div>

                <div className="dh-number-row">
                    <span className="dh-number">{animatedTotal}</span>
                    <span className="dh-number-label">
                        {vendorFilter ? (
                            <>cotizaciones de <strong>{vendorFilter}</strong>.</>
                        ) : (
                            <>cotizaciones activas.</>
                        )}
                    </span>
                </div>

                {/*
                  Slashes row: solo mostramos "$ en pipeline" en V1.
                  "% conversión" y "X.X d promedio" quedan fuera porque el backend
                  no expone esas métricas todavía. Si el producto las pide, se
                  agregan en V2 (requiere endpoint nuevo o cálculo en KPIs).
                */}
                {showPipeline && (
                    <div className="dh-slashes">
                        <span><strong>{formatPipelineMXN(pipelineMXN)}</strong> en pipeline</span>
                    </div>
                )}
            </div>

            <div className="dh-right">
                <div className="dh-search">
                    <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'} />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={onSearchChange}
                    />
                </div>
                {onNewClick && (
                    <GradientRingButton onClick={onNewClick}>
                        <i className="pi pi-plus" /> Nueva
                    </GradientRingButton>
                )}
            </div>
        </div>
    );
};

export default DashboardHero;
